import { withScope, captureMessage } from '@sentry/browser';
import { TrackingEvent, Sha256LeadsUserData, LeadsUserData } from '../../../types/consultation';
import { sha256 } from 'js-sha256';
import { tariffBundleMarketingNames } from '../../../types/tariffs';

function getEmptyKeys(payload: TrackingEvent): string[] {
  const emptyKeys: string[] = [];
  const collect = (obj: Record<string, unknown>, prefix = ''): void => {
    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value !== null && typeof value === 'object') {
        collect(value as Record<string, unknown>, path);
      } else if (value === undefined || value === null || value === '') {
        emptyKeys.push(path);
      }
    }
  };
  collect(payload as unknown as Record<string, unknown>);
  return emptyKeys;
}

export async function pushToDataLayer(payload: TrackingEvent): Promise<void> {
  if (payload.leadsUserData && 'email_address' in payload.leadsUserData && payload.leadsUserData.email_address === 'testing-loki+e2e-smoke-live@on-testing.de') {
    return;
  }

  if (payload.leadsUserData && 'email_address' in payload.leadsUserData) {
    const sha256LeadsUserData: Sha256LeadsUserData = {};
    for (const [key, value] of Object.entries(payload.leadsUserData)) {
      if ((key === 'email_address' || key === 'phone_number') && typeof value === 'string') {
        sha256LeadsUserData[('sha256_' + key) as keyof Sha256LeadsUserData] = await hashValues(value);
      } else if (key === 'address' && value && typeof value === 'object') {
        sha256LeadsUserData.address = {
          ...(value.first_name && { sha256_first_name: await hashValues(value.first_name) }),
          ...(value.last_name && { sha256_last_name: await hashValues(value.last_name) })
        };
      }
    }
    payload.leadsUserData = sha256LeadsUserData;
  }

  if (payload.tariff && !Object.values(tariffBundleMarketingNames).includes(payload.tariff)) {
    withScope(scope => {
      scope.setLevel('info');
      scope.setTag('tracking', 'empty-values');
      scope.setExtras({
        keys: `invalid tariff: "${payload.tariff}"`,
        event: payload.event ?? 'unknown',
        url: window.location.href
      });
      captureMessage('dataLayer push: tariff not matching marketing tariff name!');
    });
  }

  const emptyKeys = getEmptyKeys(payload);

  if (emptyKeys.length > 0) {
    withScope(scope => {
      scope.setLevel('info');
      scope.setTag('tracking', 'empty-values');
      scope.setExtras({
        keys: emptyKeys.join(', '),
        event: payload.event ?? 'unknown',
        url: window.location.href
      });
      captureMessage('dataLayer push: empty values found!');
    });
    sanitizeEmptyValues(payload as unknown as Record<string, unknown>);
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

async function hashValues(value: string): Promise<string> {
  return value !== 'na' ? sha256(value.trim()) : value;
}

function sanitizeEmptyValues(obj: Record<string, unknown>): void {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value !== null && typeof value === 'object') {
      sanitizeEmptyValues(value as Record<string, unknown>);
    } else if (value === undefined || value === null || value === '') {
      obj[key] = 'na';
    }
  }
}
